import {useCallback, useEffect, useState} from 'react'
import {Modal, Row, Col} from 'react-bootstrap'
import {useAuth} from '../../auth'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import {UserModel} from '../../auth/core/_models'
import {
  FuturiskFreeHealthCheckupForm,
  FuturiskFreeHealthCheckupFormValues,
} from './FuturiskFreeHealthCheckupForm'
import {
  FuturiskFreeHealthCheckupData,
  FuturiskFreeHealthCheckupList,
} from './FuturiskFreeHealthCheckupList'

type Props = {
  show: boolean
  handleClose: () => void
  currentUser?: UserModel
}

const CREATE_BOOKING_URL = 'https://etl.futurisk.in/webhook/d33c8075-0c4c-4fb2-9a0e-8d498dbfa80a'
const LOAD_BOOKINGS_URL = 'https://etl.futurisk.in/webhook/5b294ee0-e589-4071-abfa-53b5785c80e3'

const masterHealthCheckupTests = [
  {
    category: 'Diabetes Profile',
    items: ['Blood Sugar Fasting (1)', 'HbA1c (1)'],
  },
  {
    category: 'Liver Function Test',
    items: ['Liver Function Test (12)'],
  },
  {
    category: 'Kidney Function Test',
    items: ['Kidney Function Test (11)'],
  },
  {
    category: 'Vitamin Profile',
    items: ['Vitamin D (1)', 'Vitamin B12'],
  },
  {
    category: 'Urine Microscopy',
    items: ['Urine Routine & Microscopy (23)'],
  },
  {
    category: 'Lipid Profile',
    items: ['Lipid Profile (9)'],
  },
  {
    category: 'CBC',
    items: ['Complete Hemogram (26)'],
  },
  {
    category: 'ESR',
    items: ['Erythrocyte Sedimentation Rate (ESR) (1)'],
  },
  {
    category: 'Thyroid Profile',
    items: ['Thyroid Profile (3)'],
  },
]

const normalizeBookingsResponse = (data: any): FuturiskFreeHealthCheckupData[] => {
  const normalizePayload = (payload: any): FuturiskFreeHealthCheckupData[] => {
    if (Array.isArray(payload)) {
      return payload.map((item) => item?.json || item).filter(Boolean)
    }

    const normalizedItem = payload?.json || payload
    if (
      normalizedItem &&
      typeof normalizedItem === 'object' &&
      (normalizedItem.id ||
        normalizedItem.ID ||
        normalizedItem.Id ||
        normalizedItem.booking_reference ||
        normalizedItem.Name ||
        normalizedItem.name)
    ) {
      return [normalizedItem]
    }

    return []
  }

  if (!data) {
    return []
  }

  if (data.success && data.bookings) {
    return normalizePayload(data.bookings)
  }

  if (data.booking) {
    return normalizePayload(data.booking)
  }

  if (data.data) {
    return normalizePayload(data.data)
  }

  return normalizePayload(data)
}

const FuturiskFreeHealthCheckupModel: React.FC<Props> = ({show, handleClose, currentUser: propsCurrentUser}) => {
  const auth = useAuth()
  const currentUser = propsCurrentUser || auth.currentUser
  const [requests, setRequests] = useState<FuturiskFreeHealthCheckupData[]>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [submissionError, setSubmissionError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const loadBookings = useCallback(async () => {
    setLoading(true)
    setSubmissionError(null)

    try {
      const payload = {
        CRMClientId: currentUser?.CRMClientId,
        EmployeeId: currentUser?.EmployeeId,
        Email: currentUser?.Email,
      }

      const response = await fetch(LOAD_BOOKINGS_URL, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Unable to load bookings: ${response.status}`)
      }

      const data = await response.json()
      const bookingList = normalizeBookingsResponse(data)

      setRequests(bookingList)
      setShowForm(bookingList.length === 0)
    } catch (error) {
      setRequests([])
      setShowForm(true)
    } finally {
      setLoading(false)
    }
  }, [currentUser?.CRMClientId, currentUser?.EmployeeId, currentUser?.Email])

  useEffect(() => {
    if (!show) {
      setRequests([])
      setLoading(false)
      setShowForm(false)
      setSubmissionError(null)
      setCreating(false)
      setShowSuccess(false)
      return
    }

    void loadBookings()
  }, [show, loadBookings])

  const handleFormSubmit = async (values: FuturiskFreeHealthCheckupFormValues) => {
    setSubmissionError(null)
    setCreating(true)

    try {
      if (currentUser?.CRMClientId == null || !currentUser?.EmployeeId) {
        throw new Error('Unable to resolve CRM client ID or employee ID from the current user')
      }

      const payload = {
        Name: values.Name,
        Age: Number(values.age),
        Gender: values.gender,
        ContactNumber: values.phoneNumber,
        EmailId: values.emailId,
        OfficialEmailId: values.officialId,
        CompleteAddress: values.completeAddress,
        Pincode: values.pincode,
        HomeCollectionDate: values.homeCollectionDate,
        HomeCollectionTime: values.homeCollectionTime,
        CRMClientId: currentUser.CRMClientId,
        EmployeeId: currentUser.EmployeeId,
      }

      const response = await fetch(CREATE_BOOKING_URL, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Server error: ${response.status} ${errorText}`)
      }

      const data = await response.json().catch(() => null)
      // After submission, always reload from the source of truth (the list API)
      await loadBookings()
      setShowSuccess(true)
    } catch (error: any) {
      setSubmissionError(error?.message || 'Booking submission failed')
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal
      className='modal fade'
      id='kt_modal_futurisk_free_health_checkup'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      aria-hidden='true'
      dialogClassName='modal-xl'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Futurisk Free Health Checkup Booking</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-3x' />
          </div>
        </div>

        <div className='modal-body'>
          {loading ? (
            <div className='text-center'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
              <div className='mt-2'>Loading booking details...</div>
            </div>
          ) : showSuccess ? (
            <div className='text-center py-10'>
              <div className='mb-5'>
                <KTSVG
                  path='/media/icons/duotune/general/gen048.svg'
                  className='svg-icon-5tx text-success'
                />
              </div>
              <h2 className='fw-bold mb-4'>Thank You!</h2>
              <p className='fs-4 text-muted mb-8'>
                Your Master Health Check-up booking has been successfully submitted.
                <br />
                Our partner will contact you shortly for the home collection.
              </p>
              <button
                type='button'
                className='btn btn-lg btn-success px-10'
                onClick={() => {
                  setShowSuccess(false)
                  void loadBookings()
                }}
              >
                View My Booking
              </button>
            </div>
          ) : showForm || requests.length === 0 ? (
            <Row>
              <Col xs={12} md={7} className='mb-5 mb-md-0'>
                <div
                  className='p-4 d-flex flex-column'
                  style={{
                    background: 'linear-gradient(135deg, #0f766e 0%, #2563eb 100%)',
                    borderRadius: '12px',
                    color: 'white',
                  }}
                >
                  <div className='mb-4'>
                    <div className='d-inline-block px-3 py-2 mb-3 rounded' style={{background: 'rgba(255,255,255,0.16)'}}>
                      Free for employees
                    </div>
                    <h3 className='fw-bold mb-2 text-white'>Book Your Home Collection Health Checkup</h3>
                  </div>

                  <div
                    className='mb-4 overflow-hidden'
                    style={{
                      borderRadius: '12px',
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.22)',
                    }}
                  >
                    <div className='table-responsive'>
                      <table className='table mb-0 align-middle' style={{background: '#ffffff', color: '#111827'}}>
                        <thead>
                          <tr>
                            <th
                              style={{
                                background: '#f5c842',
                                border: '1px solid #2f2f2f',
                                fontSize: '1rem',
                                fontWeight: 700,
                                padding: '0.75rem',
                                textAlign: 'center',
                                width: '40%',
                              }}
                            >
                              Tests
                            </th>
                            <th
                              style={{
                                background: '#f5c842',
                                border: '1px solid #2f2f2f',
                                fontSize: '1rem',
                                fontWeight: 700,
                                padding: '0.75rem',
                                textAlign: 'center',
                              }}
                            >
                              Master Health Check-up
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {masterHealthCheckupTests.map((test) => (
                            <tr key={test.category}>
                              <td
                                style={{
                                  border: '1px solid #2f2f2f',
                                  padding: '0.75rem',
                                  fontSize: '0.95rem',
                                  fontWeight: 700,
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {test.category}
                              </td>
                              <td
                                style={{
                                  border: '1px solid #2f2f2f',
                                  padding: 0,
                                  fontSize: '0.95rem',
                                  textAlign: 'center',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {test.items.map((item, itemIndex) => (
                                  <div
                                    key={item}
                                    style={{
                                      padding: '0.45rem 0.75rem',
                                      borderTop: itemIndex === 0 ? 'none' : '1px solid #2f2f2f',
                                    }}
                                  >
                                    {item}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className='p-4 rounded' style={{background: 'rgba(255,255,255,0.14)'}}>
                    <div className='fw-bold mb-2'>Booking Rules</div>
                    <div className='small' style={{opacity: 0.92}}>
                      No payment is required, only one booking is allowed per user, home collection is available from 6:00 AM to 7:00 PM, and dates can be selected from tomorrow up to the next 30 days.
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={5}>
                <FuturiskFreeHealthCheckupForm
                  submissionError={submissionError}
                  creating={creating}
                  onSubmit={handleFormSubmit}
                  currentUser={currentUser}
                />
              </Col>
            </Row>
          ) : (
            <FuturiskFreeHealthCheckupList requests={requests} />
          )}
        </div>

        <div className='modal-footer'>Only one free health checkup booking is allowed per user.</div>
      </div>
    </Modal>
  )
}

export {FuturiskFreeHealthCheckupModel}